import type {
	CancelOptions,
	CheckoutPlan,
	ExperimentData,
	PaymentMethod,
	RecordCampaignImpressionPayload,
	RevocationIntakePayload,
	SubmitPauseRequestPayload,
	UpgradePayload,
	UpdatePayload as UserUpdatePayload,
} from '@kimuno/api-sdk';
import { isEmpty } from 'lodash';

import { AsyncActionStatus } from '../actions/common.actionTypes';
import api from '../clients/api';
import cookies from '../clients/cookies';
import { userType } from '../constants';
import billingService from '../services/billing.service';
import schoolService from '../services/school.service';
import userService from '../services/user.service';
import bugsnagClient, { notifyBugsnagHandledError } from '../utils/bugsnagClient';
import type { ThunkAction } from './common.actionTypes';
import {
	ADD_SCHOOL_TEACHER,
	type AddSchoolTeacherAction,
	ASSIGN_SCHOOL_ADMIN,
	type AssignSchoolAdminAction,
	CANCEL_SUBSCRIPTION,
	type CancelSubscriptionAction,
	CREATE_SCHOOL_ADMIN,
	type CreateSchoolAdminAction,
	FETCH_ME,
	type FetchMeAction,
	GET_EXPERIMENT,
	GET_INVOICES,
	GET_PAYMENT_METHOD,
	GET_PENDING_SCHOOL_LICENCE,
	GET_SCHOOL_ADMINS,
	GET_SCHOOL_INVOICES,
	GET_SCHOOL_SEATS,
	GET_SCHOOL_TEACHERS,
	GET_STRIPE_SUBSCRIPTION_DETAILS,
	GET_SUBSCRIPTION,
	GET_SUBSCRIPTION_UPGRADE_PREVIEW,
	GET_UPGRADE_OPTIONS,
	GET_UPGRADE_PREVIEW,
	GET_USER_INVOICES,
	type GetExperimentAction,
	type GetInvoicesAction,
	type GetPaymentMethodAction,
	type GetPendingSchoolLicenceAction,
	type GetSchoolAdminsAction,
	type GetSchoolInvoicesAction,
	type GetSchoolSeatsAction,
	type GetSchoolTeachersAction,
	type GetStripeSubscriptionDetailsAction,
	type GetSubscriptionAction,
	type GetSubscriptionUpgradePreviewAction,
	type GetUpgradeOptionsAction,
	type GetUpgradePreviewAction,
	type GetUserInvoicesAction,
	INCREMENT_DOWNLOADS_COUNTER,
	type IncrementDownloadsCounterAction,
	RECORD_CAMPAIGN_IMPRESSION,
	RECORD_TRIGGERED_HINT,
	REMOVE_SCHOOL_ADMIN,
	REMOVE_SCHOOL_TEACHER,
	REMOVE_TEACHER_FROM_STORE,
	REVOKE_CANCELLATION,
	type RecordCampaignImpressionAction,
	type RecordTriggeredHintAction,
	type RemoveSchoolAdminAction,
	type RemoveSchoolTeacherAction,
	type RemoveTeacherFromStoreAction,
	type RevokeCancellationAction,
	SET_SELECTED_SCHOOL,
	type SetSelectedSchoolAction,
	SUBMIT_PAUSE_REQUEST,
	SUBMIT_REVOCATION,
	type SubmitPauseRequestAction,
	type SubmitRevocationAction,
	TRACK_EXPERIMENT,
	UPDATE_ME,
	UPDATE_NEWSLETTER,
	UPDATE_PAYMENT_METHOD,
	UPDATE_SCHOOL_TEACHER,
	UPGRADE,
	UPGRADE_PLAN,
	UPGRADE_TO_KOMBI,
	type UpdateExperimentAction,
	type UpdateMeAction,
	type UpdateNewsletterAction,
	type UpdatePaymentMethodAction,
	type UpdateSchoolTeacherAction,
	type UpgradeAction,
	type UpgradePlanAction,
	type UpgradeToKombiAction,
} from './user.actionTypes';

export function fetchMe(
	sendRequest = false,
	options?: {
		/**
		 * Skip the START dispatch so the global user status never leaves READY.
		 * For background refreshes (e.g. the report card refetching progress on
		 * open): loading gates like useRedirectIfNoAIAssistantAccess treat
		 * LOADING as "swap the page for a loader", which unmounts whatever the
		 * refresh was triggered from.
		 */
		silent?: boolean;
	}
): ThunkAction<FetchMeAction> {
	return async (dispatch, getState) => {
		// After successfully upgrading the subscription, we need to get updated user data to show the correct buttons/etc
		// on the page. For that purpose we use the "sendRequest" variable to send additional request to the API.
		if (!sendRequest && getState().user.me) {
			return;
		}

		if (!options?.silent) {
			dispatch({ type: FETCH_ME.START });
		}

		await userService
			.getMe()
			.then((result) => {
				dispatch({ type: FETCH_ME.SUCCESS, result });
				return result;
			})
			.then((result) => {
				if (result) {
					bugsnagClient.user = {
						id: result._id,
					};
				}
			})
			.catch((error) => {
				if (error.statusCode === 401) {
					cookies.deleteAuthToken();
					dispatch({ type: FETCH_ME.EXPIRED, error });
				}
				dispatch({ type: FETCH_ME.ERROR, error });
			});
	};
}

export function updateMe(payload: UserUpdatePayload): ThunkAction<UpdateMeAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;

		if (!me) {
			return;
		}
		if (isEmpty(payload)) {
			return;
		}
		const userId = me._id;

		dispatch({ type: UPDATE_ME.START, payload });

		return userService
			.updateUser(userId, payload)
			.then((result) => dispatch({ type: UPDATE_ME.SUCCESS, result, payload }))
			.catch((error) => {
				dispatch({ error, type: UPDATE_ME.ERROR, payload });
				throw error;
			});
	};
}

export function updateNewsletter(optIn: boolean): ThunkAction<UpdateNewsletterAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;

		if (!me) {
			return;
		}
		const userId = me._id;

		dispatch({ type: UPDATE_NEWSLETTER.START, payload: { optIn } });

		return userService
			.updateNewsletter(userId, { optin: optIn })
			.catch((error) => dispatch({ type: UPDATE_NEWSLETTER.ERROR, payload: { optIn }, error }));
	};
}

export function recordTriggeredHint(hint: string): ThunkAction<RecordTriggeredHintAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;

		if (!me) {
			return;
		}

		const triggeredHints = me.data?.triggeredHints ?? [];
		if (triggeredHints.includes(hint)) {
			return;
		}

		const userId = me._id;

		dispatch({ type: RECORD_TRIGGERED_HINT.START, payload: { hint } });

		return userService
			.recordTriggeredHint(userId, { hint })
			.then((result) => dispatch({ type: RECORD_TRIGGERED_HINT.SUCCESS, payload: { hint }, result }))
			.catch((error) => dispatch({ type: RECORD_TRIGGERED_HINT.ERROR, payload: { hint }, error }));
	};
}

export function recordCampaignImpression(
	payload: RecordCampaignImpressionPayload
): ThunkAction<RecordCampaignImpressionAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;

		if (!me) {
			return;
		}

		dispatch({ type: RECORD_CAMPAIGN_IMPRESSION.START, payload });

		return userService
			.recordCampaignImpression(me._id, payload)
			.then((result) => dispatch({ type: RECORD_CAMPAIGN_IMPRESSION.SUCCESS, payload, result }))
			.catch((error) => dispatch({ type: RECORD_CAMPAIGN_IMPRESSION.ERROR, payload, error }));
	};
}

export function updateExperiment(experiment: ExperimentData): ThunkAction<UpdateExperimentAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me) {
			return;
		}
		const userId = me._id;
		const payload = { experiment };

		dispatch({ type: TRACK_EXPERIMENT.START, payload });
		return userService
			.updateExperiment(userId, experiment)
			.then((result) => dispatch({ type: TRACK_EXPERIMENT.SUCCESS, result, payload }))
			.catch((error) => dispatch({ type: TRACK_EXPERIMENT.ERROR, payload, error }));
	};
}

export function getExperiment(experiment: string): ThunkAction<GetExperimentAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me) {
			return;
		}
		const userId = me._id;
		const payload = { experiment };

		dispatch({ type: GET_EXPERIMENT.START, payload });
		return userService
			.getExperiment(userId, experiment)
			.then((result) => dispatch({ type: GET_EXPERIMENT.SUCCESS, result, payload }))
			.catch((error) => dispatch({ type: GET_EXPERIMENT.ERROR, payload, error }));
	};
}

export function getSubscription(): ThunkAction<GetSubscriptionAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;

		if (!me) {
			return;
		}

		const userId = me._id;
		const payload = { userId };

		// SM-255: Stripe users have no Zuora record, so the legacy getSubscription
		// always 404s for them. Skip the dead request, but still settle
		// subscriptionStatus to NOTFOUND exactly as the 404 path did — downstream
		// render gates (PricingPage / NewPricingPage / PaymentPage) wait for this to
		// leave LOADING, so short-circuiting silently would hang those pages.
		if (me.payment?.stripeCustomerId) {
			dispatch({
				type: GET_SUBSCRIPTION.SUCCESS,
				result: undefined,
				payload: { ...payload, status: AsyncActionStatus.NOTFOUND },
			});
			return;
		}

		dispatch({ type: GET_SUBSCRIPTION.START, payload });

		return billingService
			.getSubscription(userId)
			.then((result) => dispatch({ type: GET_SUBSCRIPTION.SUCCESS, result, payload }))
			.catch((error) => {
				// We get a 404 for basis-users without a subscription (not suspended or paused)
				// Don't treat it as an error
				// Set subscription to undefined
				if (error.statusCode === 404) {
					dispatch({
						type: GET_SUBSCRIPTION.SUCCESS,
						result: undefined,
						payload: { ...payload, status: AsyncActionStatus.NOTFOUND },
					});
					return;
				}

				dispatch({ type: GET_SUBSCRIPTION.ERROR, payload, error });
			});
	};
}

export function fetchSubscriptionUpgradePreview(
	licenceModelId: number
): ThunkAction<GetSubscriptionUpgradePreviewAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;

		if (!me) {
			return;
		}

		const userId = me._id;
		const payload = { userId, licenceModelId };

		dispatch({ type: GET_SUBSCRIPTION_UPGRADE_PREVIEW.START, payload });

		return billingService
			.getSubscriptionUpgradePreview(userId, licenceModelId)
			.then((result) => dispatch({ type: GET_SUBSCRIPTION_UPGRADE_PREVIEW.SUCCESS, result, payload }))
			.catch((error) => dispatch({ type: GET_SUBSCRIPTION_UPGRADE_PREVIEW.ERROR, payload, error }));
	};
}

export function cancelSubscription(
	cancelOptions?: CancelOptions
): ThunkAction<CancelSubscriptionAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me || me.type === userType.basis) {
			return;
		}
		const userId = me._id;
		const payload = { userId, cancelOptions };
		dispatch({ type: CANCEL_SUBSCRIPTION.START, payload });
		return billingService
			.cancelSubscription(userId, cancelOptions)
			.then(() => dispatch(getSubscription()))
			.then((result) => dispatch({ type: CANCEL_SUBSCRIPTION.SUCCESS, result, payload }))
			.catch((error) => {
				dispatch({ type: CANCEL_SUBSCRIPTION.ERROR, payload, error });
				throw error;
			});
	};
}

export function getUpgradeOptions(): ThunkAction<GetUpgradeOptionsAction> {
	return async (dispatch) => {
		const payload = {};
		dispatch({ type: GET_UPGRADE_OPTIONS.START, payload });
		return billingService
			.getUpgradeOptions()
			.then((result) => {
				dispatch({ type: GET_UPGRADE_OPTIONS.SUCCESS, result, payload });
				return result;
			})
			.catch((error) => {
				dispatch({ type: GET_UPGRADE_OPTIONS.ERROR, payload, error });
				throw error;
			});
	};
}

export function upgradePlan(targetPlanKey: CheckoutPlan): ThunkAction<UpgradePlanAction> {
	return async (dispatch) => {
		const payload = { targetPlanKey };
		dispatch({ type: UPGRADE_PLAN.START, payload });
		return billingService
			.upgradePlan({ targetPlanKey })
			.then((result) => {
				dispatch({ type: UPGRADE_PLAN.SUCCESS, result, payload });
				return result;
			})
			.catch((error) => {
				dispatch({ type: UPGRADE_PLAN.ERROR, payload, error });
				throw error;
			});
	};
}

export function getUpgradePreview(
	targetPlanKey: CheckoutPlan
): ThunkAction<GetUpgradePreviewAction> {
	return async (dispatch) => {
		const payload = { targetPlanKey };
		dispatch({ type: GET_UPGRADE_PREVIEW.START, payload });
		return billingService
			.getUpgradePreview(targetPlanKey)
			.then((result) => {
				dispatch({ type: GET_UPGRADE_PREVIEW.SUCCESS, result, payload });
				return result;
			})
			.catch((error) => {
				dispatch({ type: GET_UPGRADE_PREVIEW.ERROR, payload, error });
				throw error;
			});
	};
}

// SM-92 Phase 6 — fetch the Stripe-side subscription state (status, trialEndAt,
// cancelAt, ...) and hydrate it into Redux so global gates (e.g. hiding the
// side-nav upgrade button while trialing) can react to it.
export function getStripeSubscriptionDetails(): ThunkAction<GetStripeSubscriptionDetailsAction> {
	return async (dispatch) => {
		const payload = {};
		dispatch({ type: GET_STRIPE_SUBSCRIPTION_DETAILS.START, payload });
		return billingService
			.getSubscriptionDetails()
			.then((result) => {
				dispatch({ type: GET_STRIPE_SUBSCRIPTION_DETAILS.SUCCESS, result, payload });
				return result;
			})
			.catch((error) => {
				dispatch({ type: GET_STRIPE_SUBSCRIPTION_DETAILS.ERROR, payload, error });
				throw error;
			});
	};
}

// SM-117 — submit a 14-day right-of-withdrawal (Widerruf) request for the current
// user's Stripe subscription. On success we re-fetch the subscription details so
// the revocation block (and its `request` state) is refreshed and the UI shows
// the "withdrawal is being processed" alert without a full reload.
export function submitRevocation(
	payload: RevocationIntakePayload = {}
): ThunkAction<SubmitRevocationAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me) {
			// No authenticated user → nothing was submitted. Surface it as a failure so
			// the caller (WithdrawSubDialog) does not show a false success toast.
			const error = new Error('Cannot submit revocation: no authenticated user');
			dispatch({ type: SUBMIT_REVOCATION.ERROR, payload, error });
			throw error;
		}

		dispatch({ type: SUBMIT_REVOCATION.START, payload });
		return billingService
			.submitRevocation(payload)
			.then((result) => {
				dispatch({ type: SUBMIT_REVOCATION.SUCCESS, result, payload });
				// Refresh the revocation block so the "being processed" alert appears.
				// A refresh failure is non-fatal — the request was already accepted, so it
				// must NOT surface as an unsuccessful withdrawal (the stale block self-heals
				// on the next page load / fetch).
				dispatch(getStripeSubscriptionDetails()).catch(() => {
					/* non-fatal */
				});
				return result;
			})
			.catch((error) => {
				dispatch({ type: SUBMIT_REVOCATION.ERROR, payload, error });
				throw error;
			});
	};
}

// SM-411 — submit a pause request for the current user's Stripe subscription.
// On success we re-fetch the subscription details so the newly embedded `pause`
// block (SM-411 backend) is refreshed and the UI flips to the "Pause angefordert"
// state without a full reload — mirrors the SM-117 submitRevocation flow.
export function submitPauseRequest(
	payload: SubmitPauseRequestPayload
): ThunkAction<SubmitPauseRequestAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me) {
			// No authenticated user → nothing was submitted. Surface it as a failure so
			// the caller (PauseSubDialog) does not show a false success toast.
			const error = new Error('Cannot submit pause request: no authenticated user');
			dispatch({ type: SUBMIT_PAUSE_REQUEST.ERROR, payload, error });
			throw error;
		}

		dispatch({ type: SUBMIT_PAUSE_REQUEST.START, payload });
		return billingService
			.submitPauseRequest(payload)
			.then((result) => {
				dispatch({ type: SUBMIT_PAUSE_REQUEST.SUCCESS, result, payload });
				// Refresh the subscription-details slice so the `pause` block populates
				// and the chip flips to "Pause angefordert". A refresh failure is
				// non-fatal — the row was already accepted server-side; the block
				// self-heals on the next fetch.
				dispatch(getStripeSubscriptionDetails()).catch(() => {
					/* non-fatal */
				});
				return result;
			})
			.catch((error) => {
				dispatch({ type: SUBMIT_PAUSE_REQUEST.ERROR, payload, error });
				throw error;
			});
	};
}

// SUB-58 — take back a pending scheduled cancellation for the current user's
// Stripe subscription. On success we re-fetch the subscription details so the
// "verfügbar bis" banner and status chip clear without a full reload. A
// `no-pending-cancellation` outcome also travels this success path: there was
// nothing left to revoke, which is an informational result, not a failure.
export function revokeCancellation(): ThunkAction<RevokeCancellationAction> {
	return async (dispatch, getState) => {
		const payload = {};
		const me = getState().user.me;
		if (!me) {
			// No authenticated user → nothing was revoked. Surface it as a failure so
			// the caller does not show a false success toast.
			const error = new Error('Cannot revoke cancellation: no authenticated user');
			dispatch({ type: REVOKE_CANCELLATION.ERROR, payload, error });
			throw error;
		}

		dispatch({ type: REVOKE_CANCELLATION.START, payload });
		return billingService
			.revokeCancellation()
			.then((result) => {
				dispatch({ type: REVOKE_CANCELLATION.SUCCESS, result, payload });
				// A refresh failure is non-fatal — the revoke already landed server-side,
				// so it must NOT surface as an unsuccessful revoke; the stale banner
				// self-heals on the next fetch / page load.
				dispatch(getStripeSubscriptionDetails()).catch(() => {
					/* non-fatal */
				});
				return result;
			})
			.catch((error) => {
				dispatch({ type: REVOKE_CANCELLATION.ERROR, payload, error });
				throw error;
			});
	};
}

export function getPaymentMethod(): ThunkAction<GetPaymentMethodAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;

		if (!me || me.type === userType.basis) {
			return;
		}

		// SM-255: Stripe users have no Zuora payment method — the legacy
		// getPaymentMethod always 404s for them. Nothing downstream waits on this
		// (the account page's payment sections are hidden for Stripe users), so we
		// can skip the dead request outright.
		if (me.payment?.stripeCustomerId) {
			return;
		}

		const userId = me._id;
		const payload = { userId };

		dispatch({ type: GET_PAYMENT_METHOD.START, payload });
		return api.billing
			.getPaymentMethod(userId)
			.then((result) => {
				return dispatch({ type: GET_PAYMENT_METHOD.SUCCESS, result, payload });
			})
			.catch((error) => dispatch({ type: GET_PAYMENT_METHOD.ERROR, payload, error }));
	};
}

export function updatePaymentMethod(
	paymentMethod: PaymentMethod
): ThunkAction<UpdatePaymentMethodAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;

		if (!me || me.type === userType.basis) {
			return;
		}

		const userId = me._id;
		const payload = { paymentMethod, userId };

		dispatch({ type: UPDATE_PAYMENT_METHOD.START, payload });
		return api.billing
			.updatePaymentMethod(userId, paymentMethod)
			.then((result) => dispatch({ type: UPDATE_PAYMENT_METHOD.SUCCESS, result, payload }))
			.catch((error) => dispatch({ type: UPDATE_PAYMENT_METHOD.ERROR, payload, error }));
	};
}

export function getInvoices(): ThunkAction<GetInvoicesAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		const invoiceHistory = getState().user.invoiceHistory;

		if (!me || invoiceHistory) {
			return;
		}

		const userId = me._id;

		dispatch({ type: GET_INVOICES.START });

		return billingService
			.getInvoices(userId)
			.then((result) => dispatch({ type: GET_INVOICES.SUCCESS, result }))
			.catch((error) => dispatch({ type: GET_INVOICES.ERROR, error }));
	};
}

export function getUserInvoices(): ThunkAction<GetUserInvoicesAction> {
	return async (dispatch, getState) => {
		const { me, userInvoices, userInvoicesStatus } = getState().user;

		if (!me || userInvoices || userInvoicesStatus === AsyncActionStatus.LOADING) {
			return;
		}

		const payload = { userId: me._id };

		dispatch({ type: GET_USER_INVOICES.START, payload });

		return billingService
			.getUserInvoices()
			.then((result) => dispatch({ type: GET_USER_INVOICES.SUCCESS, payload, result }))
			.catch((error) => dispatch({ type: GET_USER_INVOICES.ERROR, payload, error }));
	};
}

// Asked once per login (the slice resets on LOGIN / LOGOUT). A failure shows
// nothing and is reported, since the proxy answers 502 until its passthrough
// ships and that must never surface to the teacher.
export function getPendingSchoolLicence(): ThunkAction<GetPendingSchoolLicenceAction> {
	return async (dispatch, getState) => {
		const { me, pendingSchoolLicence, pendingSchoolLicenceStatus } = getState().user;

		if (
			!me ||
			pendingSchoolLicence !== undefined ||
			pendingSchoolLicenceStatus === AsyncActionStatus.LOADING
		) {
			return;
		}

		const payload = { userId: me._id };

		dispatch({ type: GET_PENDING_SCHOOL_LICENCE.START, payload });

		return billingService
			.getPendingSchoolLicence()
			.then((result) => dispatch({ type: GET_PENDING_SCHOOL_LICENCE.SUCCESS, payload, result }))
			.catch((error) => {
				dispatch({ type: GET_PENDING_SCHOOL_LICENCE.ERROR, payload, error });
				notifyBugsnagHandledError(error, { user: payload });
			});
	};
}

export function getSchoolInvoices(
	schoolId: string,
	page = 1,
	pageSize = 10
): ThunkAction<GetSchoolInvoicesAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;

		if (!me || !me.rights?.schoolAdmin) {
			return;
		}

		dispatch({ type: GET_SCHOOL_INVOICES.START });

		return schoolService
			.getSchoolInvoices(schoolId, page, pageSize)
			.then((result) => dispatch({ type: GET_SCHOOL_INVOICES.SUCCESS, result }))
			.catch((error) => dispatch({ type: GET_SCHOOL_INVOICES.ERROR, error }));
	};
}

export function upgradeToDocuments(
	userId: string,
	payload: UpgradePayload
): ThunkAction<UpgradeAction> {
	return async (dispatch) => {
		dispatch({ type: UPGRADE.START, payload });

		return userService
			.upgradeToDocuments(userId, payload)
			.then((user) => dispatch({ type: UPGRADE.SUCCESS, result: user, payload }));
	};
}

export function upgradeToKombi(
	userId: string,
	payload: UpgradePayload
): ThunkAction<UpgradeToKombiAction> {
	return async (dispatch) => {
		dispatch({ type: UPGRADE_TO_KOMBI.START, payload });

		return userService
			.upgradeToKombi(userId, payload)
			.then((user) => dispatch({ type: UPGRADE_TO_KOMBI.SUCCESS, result: user, payload }));
	};
}

export function incrementDownloadsCounter(): ThunkAction<IncrementDownloadsCounterAction> {
	return async (dispatch) => {
		dispatch({ type: INCREMENT_DOWNLOADS_COUNTER });
	};
}

export function getSchoolTeachers(
	schoolId: string,
	page = 1,
	pageSize = 20,
	search = '',
	refreshCache = false
): ThunkAction<GetSchoolTeachersAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me || !me.rights?.schoolAdmin) {
			return;
		}

		dispatch({ type: GET_SCHOOL_TEACHERS.START });

		return schoolService
			.getSchoolTeachers(schoolId, page, pageSize, search, refreshCache)
			.then((result) => dispatch({ type: GET_SCHOOL_TEACHERS.SUCCESS, result }))
			.catch((error) => dispatch({ type: GET_SCHOOL_TEACHERS.ERROR, error }));
	};
}

export function getSchoolAdmins(schoolId: string): ThunkAction<GetSchoolAdminsAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me || !me.rights?.schoolAdmin) {
			return;
		}

		dispatch({ type: GET_SCHOOL_ADMINS.START });

		return schoolService
			.getSchoolAdmins(schoolId)
			.then((result) => {
				return dispatch({ type: GET_SCHOOL_ADMINS.SUCCESS, result });
			})
			.catch((error) => dispatch({ type: GET_SCHOOL_ADMINS.ERROR, error }));
	};
}

export function getSchoolSeats(schoolId: string): ThunkAction<GetSchoolSeatsAction> {
	return async (dispatch) => {
		dispatch({ type: GET_SCHOOL_SEATS.START });

		return schoolService
			.getSchoolSeatsOverview(schoolId)
			.then((result) => dispatch({ type: GET_SCHOOL_SEATS.SUCCESS, result }))
			.catch((error) => dispatch({ type: GET_SCHOOL_SEATS.ERROR, error }));
	};
}

export function addSchoolTeacher(
	schoolId: string,
	firstName: string,
	lastName: string,
	email: string,
	licenceModelId: number
): ThunkAction<AddSchoolTeacherAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me || !me.rights?.schoolAdmin) {
			return;
		}

		dispatch({ type: ADD_SCHOOL_TEACHER.START });

		return schoolService
			.addTeacher(schoolId, { firstName, lastName, email, licenceModelId })
			.then((result) => dispatch({ type: ADD_SCHOOL_TEACHER.SUCCESS, result }))
			.catch((error) => dispatch({ type: ADD_SCHOOL_TEACHER.ERROR, error }));
	};
}

export function updateSchoolTeacher(
	userId: string,
	schoolId: string,
	firstName: string,
	lastName: string,
	email: string
): ThunkAction<UpdateSchoolTeacherAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me || !me.rights?.schoolAdmin) {
			return;
		}

		dispatch({ type: UPDATE_SCHOOL_TEACHER.START });

		return schoolService
			.updateTeacher(userId, schoolId, firstName, lastName, email)
			.then((result) => dispatch({ type: UPDATE_SCHOOL_TEACHER.SUCCESS, result }))
			.catch((error) => dispatch({ type: UPDATE_SCHOOL_TEACHER.ERROR, error }));
	};
}

export function removeSchoolTeacher(
	userId: string,
	schoolId: string
): ThunkAction<RemoveSchoolTeacherAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me || !me.rights?.schoolAdmin) {
			return;
		}

		dispatch({ type: REMOVE_SCHOOL_TEACHER.START });

		return schoolService
			.removeTeacher(userId, schoolId)
			.then((result) => dispatch({ type: REMOVE_SCHOOL_TEACHER.SUCCESS, result }))
			.catch((error) => dispatch({ type: REMOVE_SCHOOL_TEACHER.ERROR, error }));
	};
}

export function removeSchoolAdmin(
	adminId: string,
	schoolId: string
): ThunkAction<RemoveSchoolAdminAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me || !me.rights?.schoolAdmin) {
			return;
		}

		dispatch({ type: REMOVE_SCHOOL_ADMIN.START });

		return schoolService
			.removeSchoolAdmin(adminId, schoolId)
			.then((result) => dispatch({ type: REMOVE_SCHOOL_ADMIN.SUCCESS, result }))
			.catch((error) => dispatch({ type: REMOVE_SCHOOL_ADMIN.ERROR, error }));
	};
}

export function assignSchoolAdmin(
	userId: string,
	schoolId: string
): ThunkAction<AssignSchoolAdminAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me || !me.rights?.schoolAdmin) {
			return;
		}

		dispatch({ type: ASSIGN_SCHOOL_ADMIN.START });

		return schoolService
			.assignSchoolAdmin(userId, schoolId)
			.then((result) => dispatch({ type: ASSIGN_SCHOOL_ADMIN.SUCCESS, result }))
			.catch((error) => dispatch({ type: ASSIGN_SCHOOL_ADMIN.ERROR, error }));
	};
}

export function removeTeacherFromStore(userId: string): RemoveTeacherFromStoreAction {
	return {
		type: REMOVE_TEACHER_FROM_STORE.REMOVE,
		payload: { userId },
	};
}

export function createSchoolAdmin(
	firstName: string,
	lastName: string,
	email: string,
	schoolId: string
): ThunkAction<CreateSchoolAdminAction> {
	return async (dispatch, getState) => {
		const me = getState().user.me;
		if (!me || !me.rights?.schoolAdmin) {
			return;
		}

		dispatch({ type: CREATE_SCHOOL_ADMIN.START });

		return schoolService
			.createSchoolAdmin({ firstName, lastName, email, schoolId })
			.then((result) => dispatch({ type: CREATE_SCHOOL_ADMIN.SUCCESS, result }))
			.catch((error) => dispatch({ type: CREATE_SCHOOL_ADMIN.ERROR, error }));
	};
}

export function setSelectedSchool(schoolId: string): SetSelectedSchoolAction {
	return {
		type: SET_SELECTED_SCHOOL.ACTION,
		payload: { schoolId },
	};
}
